<template>
  <div class="asc-scc-details-template">
    <v-row v-if="!subscriptionDetailsLoading" no-gutters>
      <v-col
        v-if="singleAirlineUser"
        cols="12"
        sm="12"
        class="asc-scc-details-template--singleSelect"
      >
        <asc-autocomplete-selector
          v-model="selectedItems"
          :items="getSingleAirlineSubscriptionOptions"
          :label="selectionLabel"
          display-clear
          component-type="multiple"
          required
          :sort-items="false"
          @input="handleUpdateSelections"
        ></asc-autocomplete-selector>
      </v-col>
      <v-col
        v-else
        cols="12"
        sm="12"
        class="asc-scc-details-template--grid asc-scc-details-template--filter"
      >
        <div class="asc-scc-details-template--filter-options">
          <v-radio-group
            v-model="checkedOption"
            hide-details
            row
            mandatory
            @change="handleFilterChange"
          >
            <v-radio
              v-for="option in filterOptions"
              :key="option.label"
              :label="`Filter by ${option.label}`"
              :value="option.value"
            ></v-radio>
          </v-radio-group>
        </div>
        <asc-subscription-center-details-table
          class="asc-scc-details-template--filter-grid"
          :items="subscriptionOptions"
          :groups="groups"
          :selection-label="selectionLabel"
          v-on="$listeners"
        ></asc-subscription-center-details-table>
      </v-col>
    </v-row>
    <v-row
      v-if="getNotifications && getNotifications.length"
      class="asc-scc-details-template--selections asc-scc-details-template--selections-notifications"
    >
      <v-col cols="12" sm="12">
        <h4 class="asc-scc-details-template--selections-title">
          <asc-required-label
            label="Notification Preferences"
            uppercase
            required
          />
        </h4>
        <p class="asc-scc-details-template--selections-description">
          Select one or more of the following preferences for which you would
          like to receive notifications. Email notifications will be sent
          immediately upon occurrence.
        </p>
        <div class="asc-scc-details-template--selections-notifications--items">
          <div
            v-for="notification in notifications"
            :key="notification.emailTypeId"
            class="asc-scc-details-template--selections-notifications--items-item"
          >
            <asc-checkbox
              v-model="notification.isEnabled"
              class="asc-scc-details-template--selections-notifications--items-item-checkbox"
              :label="notification.label"
              :value="notification.emailTypeId"
              @change="handleUpdateNotification(notification)"
            />
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row
      class="asc-scc-details-template--selections asc-scc-details-template--selections-frequency"
    >
      <v-col cols="12" sm="12">
        <h4 class="asc-scc-details-template--selections-title">
          Email Frequency
        </h4>
        <p class="asc-scc-details-template--selections-description">
          Notifications will be immediately sent upon occurrence.
        </p>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AscCheckbox from '@/components/atoms/AscCheckbox'
import AscRequiredLabel from '@/components/atoms/AscRequiredLabel'
import AscAutocompleteSelector from '@/components/molecules/AscAutocompleteSelector'
import AscSubscriptionCenterDetailsTable from '@/components/molecules/AscSubscriptionCenterDetailsTable'

export default {
  name: 'AscSubscriptionDetailsTemplate',

  components: {
    AscCheckbox,
    AscRequiredLabel,
    AscSubscriptionCenterDetailsTable,
    AscAutocompleteSelector,
  },

  props: {
    title: {
      type: String,
      default: '',
    },
    groups: {
      type: Object,
      default: () => ({
        hasGroups: false,
      }),
    },
    headerGroup: {
      type: Boolean,
    },
    subscriptionOptions: {
      type: Array,
      default: () => [],
    },
    selectionLabel: {
      type: String,
      default: '',
    },
    singleAirlineUser: {
      type: Boolean,
    },
    filterOptions: {
      type: Array,
      required: true,
    },
    notifications: {
      type: Array,
      default: () => [],
    },
    subscriptionDetailsLoading: {
      type: Boolean,
    },
  },

  data() {
    return {
      loading: false,
      selectedItems: [],
      checkedOption: this.filterOptions
        ? this.filterOptions.find(option => option.checked)
        : {},
    }
  },

  computed: {
    getSingleAirlineSubscriptionOptions() {
      return this.subscriptionOptions &&
        this.subscriptionOptions.length === 1 &&
        this.subscriptionOptions[0].items
        ? this.subscriptionOptions[0].items || []
        : []
    },

    getNotifications() {
      return this.notifications.filter(
        notification => notification.visible !== false,
      )
    },
  },

  watch: {
    subscriptionOptions: {
      deep: true,
      immediate: true,
      handler() {
        this.updateSelectedItems()
      },
    },
  },

  created() {
    this.updateSelectedItems()
  },

  methods: {
    updateSelectedItems() {
      const checkedItems = []
      if (this.singleAirlineUser && this.subscriptionOptions.length) {
        this.subscriptionOptions[0].items.forEach(item => {
          if (item.checked) checkedItems.push(item.value)
        })
        this.selectedItems = checkedItems
      }
    },
    handleFilterChange() {
      this.$emit('onFilterTypeChange', this.checkedOption)
    },

    handleUpdateSelections(items) {
      if (!items.length) {
        // All are unchecked
        this.$emit(
          'onUpdateUserSelections',
          null,
          this.subscriptionOptions[0],
          false,
        )
      } else if (
        items &&
        items.length === this.getSingleAirlineSubscriptionOptions.length
      ) {
        // All are checked
        this.$emit(
          'onUpdateUserSelections',
          null,
          this.subscriptionOptions[0],
          true,
        )
      } else {
        // some are checked
        this.getSingleAirlineSubscriptionOptions.forEach(item => {
          let isItemChecked = false
          if (items.includes(item.value)) isItemChecked = true
          this.$emit(
            'onUpdateUserSelections',
            this.subscriptionOptions[0],
            item,
            isItemChecked,
          )
        })
      }
    },

    handleUpdateNotification(notification) {
      this.$emit('onNotificationUpdate', notification)
    },
  },
}
</script>

<style lang="scss">
.asc-scc-details-template {
  background-color: $primary-white;
  padding: 16px 0 16px 0;

  &--headerBackLink {
    margin-bottom: 16px;

    a {
      text-decoration: none !important;
      font-size: 14px;
      letter-spacing: 0;
      line-height: 18px;
      color: $accent-700;
    }
  }

  &--header {
    margin-bottom: 21px;

    .asc-grid-wrapper-header--container {
      margin: 0;

      .asc-header--h1 {
        font-size: 14px !important;
        font-weight: 500 !important;
        letter-spacing: 1px !important;
        line-height: 18px;
      }
    }
  }

  &--description {
    color: $gray-1000;
    font-size: 14px;
    letter-spacing: 0;
    margin: 0 0 30px 0;
    line-height: 18px;
    display: inline-block;
    width: 100%;
  }

  &--singleSelect {
    margin-top: 8px;
  }

  &--filter {
    &-options {
      margin-bottom: 20px;

      .v-input--selection-controls {
        margin-top: 0;
        padding-top: 0;
      }

      .v-input--selection-controls__input {
        transform: scale(0.8);
      }

      .v-input--radio-group.v-input--radio-group--row .v-radio {
        margin-right: 35px;
      }

      .v-label {
        font-size: 14px;
        color: $primary-black;
      }
    }
  }

  &--selections {
    margin-top: 22px !important;

    &-title {
      margin: 0 0 18px 0;
      color: $gray-700;
      font-weight: normal;
      font-size: 13px !important;
      letter-spacing: 0.1px !important;
      line-height: 17px;
      text-transform: uppercase;
    }

    &-description {
      color: $primary-black;
      font-size: 14px;
      letter-spacing: 0;
      line-height: 18px;
      margin: 0;
    }

    .v-icon {
      color: $gray-600;
    }
  }

  &--selections-notifications {
    &--items {
      &-item {
        margin-top: 12px;
        padding-top: 0;

        .v-label {
          color: $primary-black;
          font-size: 14px;
          letter-spacing: 0;
          line-height: 18px;
        }
      }
    }
  }

  &--selections-frequency {
    margin-top: 23px !important;
  }
}
</style>
